/**
 * №16.1 Stage C — the step inspector. General · Schedule · Applies · Action ·
 * Manifest. Whole-field commit → one updateStep (or the child-collection op).
 *
 * №16.1.4.1 (D-AH1) — step KIND is DERIVED, never declared. The author states WHO
 * (General: role/person/AI-teammate — a task either way) and WHETHER IT WAITS
 * (one toggle → a signal completes it). The engine derives the executor; there is
 * NO "what kind of step is this?" picker. Automation is EFFECTS ("when this step
 * completes → set …"), attachable regardless of who completes it. A pre-existing
 * `nativeOp` system step RENDERS read-only (authored via the command strip/MCP).
 */
import { useState } from "react";
import type { SegmentView, StepView, TemplateDetail, WfPatch, ActionCatalog, NativeOp } from "../../hooks/use-workflow-templates";
import { findingsFor } from "./wf-author-shared";
import { fieldLabel } from "./wf-fields-shared";
import { ConditionsEditor, SignalsEditor, EffectsEditor } from "./AuthorEditors";
import { AttributeSelect, WorkspaceRoleSelect, AgentSelect } from "./FieldPickers";
import { MemberPicker } from "../MemberPicker";
import type { Selection } from "./TemplateCanvas";

type Patch = (patches: WfPatch[]) => void;
const TABS = ["General", "Schedule", "Applies", "Action", "Manifest"];

// The assignee kind, in the maker's language (agent = a №15 AI teammate). This is
// the ONLY authored control that filters execution (D-AH1 rule 1) — and it never
// changes the executor beyond task-vs-task; the agent picks the task up like a person.
const ASSIGNEE_KINDS: { value: string; label: string }[] = [
  { value: "role", label: "Role" }, { value: "person", label: "Person" }, { value: "agent", label: "AI teammate" },
];

export function StepInspector({ seg, step, detail, patch, busy, catalog, onSelect }: {
  seg: SegmentView; step: StepView; detail: TemplateDetail; patch: Patch; busy: boolean; catalog: ActionCatalog | undefined; onSelect: (s: Selection | null) => void;
}) {
  const [tab, setTab] = useState(0);
  const up = (fields: Record<string, unknown>) => patch([{ op: "updateStep", stepNo: step.stepNo, ...fields }]);
  const subjectType = detail.template?.subjectType;
  const isWait = step.action === "wait";
  const isSystem = step.action === "nativeOp"; // render-only (D-AH1 rule 4)

  return (
    <div className="wfa-insp">
      <div className="kind">Step{step.block ? ` · from block ${step.block.code} v${step.block.version}` : ""}</div>
      <div className="title">{step.name}</div>
      <div className="wfa-itabs">{TABS.map((t, i) => <button key={t} className={i === tab ? "on" : ""} onClick={() => setTab(i)}>{t}</button>)}</div>

      {tab === 0 && (
        <div>
          <div className="wfa-fld"><label>Name</label><input defaultValue={step.name} key={`n${step.stepNo}${step.name}`} disabled={busy} onBlur={(e) => e.target.value !== step.name && up({ name: e.target.value })} /></div>
          <div className="wfa-fld"><label>Context (card note)</label><input defaultValue={step.description} key={`d${step.stepNo}`} disabled={busy} onBlur={(e) => up({ description: e.target.value })} /></div>
          <div className="wfa-fld"><label>Instructions (the brief — human or agent)</label><textarea rows={3} defaultValue={step.instructions} key={`i${step.stepNo}`} disabled={busy} onBlur={(e) => up({ instructions: e.target.value })} /></div>
          <div className="wfa-fld"><label>Who does it{isWait ? " (optional — who watches for it)" : ""}</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              {ASSIGNEE_KINDS.map((k) => (
                <button key={k.value} type="button" className={`wfa-btn ${step.assigneeKind === k.value ? "primary" : ""}`} disabled={busy}
                  onClick={() => step.assigneeKind !== k.value && up({ assigneeKind: k.value, assigneeCode: null })} style={{ flex: 1 }}>{k.label}</button>
              ))}
            </div>
            {step.assigneeKind === "role" && <WorkspaceRoleSelect value={step.assigneeCode ?? ""} onChange={(v) => up({ assigneeCode: v || null })} busy={busy} />}
            {step.assigneeKind === "person" && <MemberPicker value={step.assigneeCode ?? null} onChange={(id) => up({ assigneeCode: id })} allowAgents={false} placeholder="Pick a person" />}
            {step.assigneeKind === "agent" && <AgentSelect value={step.assigneeCode ?? ""} onChange={(v) => up({ assigneeCode: v || null })} busy={busy} />}
          </div>
          <div className="wfa-fld"><label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="checkbox" style={{ width: "auto" }} checked={step.optional} disabled={busy} onChange={() => up({ optional: !step.optional })} /> Optional (not required for the segment)</label></div>
          <div className="wfa-fld"><label>Move to segment</label>
            <select value={step.segmentNo} disabled={busy} onChange={(e) => up({ segmentNo: Number(e.target.value) })}>{detail.segments.map((s) => <option key={s.segmentNo} value={s.segmentNo}>{s.name}</option>)}</select>
          </div>
        </div>
      )}

      {tab === 1 && (
        <div>
          <div className="wfa-fld"><label>Due anchor</label>
            <select value={step.dueTriggerKind ?? ""} disabled={busy} onChange={(e) => up({ dueTriggerKind: e.target.value || null })}>
              <option value="">none (opens with the segment)</option><option value="date">a date type</option><option value="step">another step completes</option>
            </select>
          </div>
          {step.dueTriggerKind === "date" && <div className="wfa-fld"><label>Date field</label><AttributeSelect subjectType={subjectType} dateOnly value={step.dueTriggerDateType ?? ""} onChange={(v) => up({ dueTriggerDateType: v })} busy={busy} /></div>}
          <div className="wfa-fld"><label>Offset (days, signed)</label><input type="number" defaultValue={step.dueOffsetDays} key={`do${step.stepNo}`} disabled={busy} onBlur={(e) => up({ dueOffsetDays: Number(e.target.value) })} style={{ width: 100 }} /></div>
          <div className="wfa-note">Step-level timing only — the segment's own trigger is its “opens when”, edited on the canvas.</div>
        </div>
      )}

      {tab === 2 && <ConditionsEditor conditions={step.conditions} ownerKind="step" ownerNo={step.stepNo} logic={step.conditionLogic} catalog={catalog} subjectType={subjectType} patch={patch} busy={busy} />}

      {tab === 3 && (
        isSystem
          ? <SystemStepPanel nativeOps={catalog?.nativeOps ?? []} params={(step.params ?? {}) as Record<string, unknown>} />
          : (
            <div>
              {/* Derived summary — the author never names an executor (D-AH1). */}
              <div className="wfa-trigbox" style={{ marginBottom: 12 }}>
                <div className="th">{isWait ? "◷ Waits for the outside world" : "● This becomes a task"}</div>
                <div className="wfa-note" style={{ marginTop: 0 }}>
                  {isWait
                    ? "A signal completes it — or someone marks it received. No one has to do it."
                    : <>Done by <b>{assigneeSummary(step)}</b> (set on the <b>General</b> tab). An AI teammate picks it up the same way a person does.</>}
                </div>
              </div>
              <div className="wfa-fld"><label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" style={{ width: "auto" }} checked={isWait} disabled={busy}
                  onChange={() => up({ action: isWait ? "humanTask" : "wait" })} /> This step waits for the outside world
              </label>
                <div className="wfa-note" style={{ marginTop: 4 }}>On, nobody performs it — it arms a signal and completes when the fact arrives (a person can always <b>Mark received</b>).</div>
              </div>
              {isWait && <div style={{ marginTop: 12 }}><SignalsEditor stepNo={step.stepNo} binding={step.binding} patch={patch} busy={busy} /></div>}
            </div>
          )
      )}

      {tab === 4 && (
        <div>
          <div className="wfa-k">DOCUMENT MANIFEST</div>
          {step.manifest.map((m) => (
            <div key={m.itemNo} className="wfa-cond"><span className="subj">{m.description}</span><span className="op">{m.direction}{m.mandatory ? " · required" : ""}</span><button className="del" style={{ marginLeft: "auto" }} disabled={busy} onClick={() => patch([{ op: "removeManifestItem", stepNo: step.stepNo, itemNo: m.itemNo }])}>✕</button></div>
          ))}
          <ManifestAdd stepNo={step.stepNo} nextNo={Math.max(0, ...step.manifest.map((m) => m.itemNo)) + 1} patch={patch} busy={busy} />
          <div className="wfa-k" style={{ marginTop: 12 }}>PLAN LINES</div>
          {step.planLines.map((p) => (
            <div key={p.lineNo} className="wfa-cond"><span className="subj">{p.resourceKind}{p.code ? ` · ${p.code}` : ""}</span><span className="op">{p.quantity ?? "?"} @ {p.rate ?? "?"}</span><button className="del" style={{ marginLeft: "auto" }} disabled={busy} onClick={() => patch([{ op: "removePlanLine", stepNo: step.stepNo, lineNo: p.lineNo }])}>✕</button></div>
          ))}
          <div className="wfa-note">Plan lines reference the money spine, never computed by the engine.</div>
        </div>
      )}

      {/* Automation is EFFECTS, and only effects (D-AH1 rule 3) — attachable to
          this step's completion no matter who (or what) completes it. Not shown
          for a system step (it IS the automation). Always visible, like a segment's. */}
      {!isSystem && (
        <div style={{ marginTop: 18, borderTop: "1px solid var(--color-border-tertiary)", paddingTop: 14 }}>
          <EffectsEditor effects={step.effects} ownerKind="step" ownerNo={step.stepNo} catalog={catalog} subjectType={subjectType} patch={patch} busy={busy} />
        </div>
      )}

      {findingsFor(detail, undefined, step.stepNo).map((f, i) => (
        <div key={i} className="wfa-finding" style={{ border: "1px solid var(--color-border-tertiary)", borderRadius: 8, marginTop: 8 }}><span className={`sev ${f.severity}`}>{f.severity}</span><span>{f.message}</span></div>
      ))}
      <button className="wfa-btn danger" style={{ marginTop: 16, width: "100%" }} disabled={busy} onClick={() => { patch([{ op: "deleteStep", stepNo: step.stepNo }]); onSelect({ kind: "segment", segmentNo: seg.segmentNo }); }}>Delete step</button>
    </div>
  );
}

/** The derived "who does it" phrase for the summary — no executor named. */
function assigneeSummary(step: StepView): string {
  if (!step.assigneeCode && step.assigneeKind !== "role") return "whoever you assign";
  if (step.assigneeKind === "role") return step.assigneeCode ? `the ${fieldLabel(step.assigneeCode)} role` : "an unassigned role";
  if (step.assigneeKind === "agent") return "an AI teammate";
  return "an assigned person";
}

/**
 * A pre-existing `nativeOp` system step (D-AH1 rule 4) — RENDER-ONLY. The engine
 * keeps native steps (prebuilds/MCP author them); the canvas shows one honestly
 * and offers no author path. To change it, use the command strip / MCP.
 */
function SystemStepPanel({ nativeOps, params }: { nativeOps: NativeOp[]; params: Record<string, unknown> }) {
  const op = String(params.op ?? "");
  const def = nativeOps.find((n) => n.op === op);
  return (
    <div>
      <div className="wfa-trigbox" style={{ marginBottom: 12 }}>
        <div className="th">⚙ System step — runs automatically</div>
        <div className="wfa-note" style={{ marginTop: 0 }}>{def ? def.explain : "the system performs this step by itself"}. Authored via the command strip / MCP — not editable on the canvas.</div>
      </div>
      <div className="wfa-k">WHAT IT DOES</div>
      <div className="wfa-cond"><span className="subj">{def?.label ?? op ?? "system op"}</span></div>
      {def?.params.map((p) => (
        <div key={p.key} className="wfa-cond"><span className="subj">{p.label}</span><span className="val" style={{ marginLeft: "auto" }}>{String(params[p.key] ?? "—")}</span></div>
      ))}
    </div>
  );
}

function ManifestAdd({ stepNo, nextNo, patch, busy }: { stepNo: number; nextNo: number; patch: Patch; busy: boolean }) {
  const [v, setV] = useState("");
  return (
    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
      <input placeholder="e.g. Signed contract" value={v} onChange={(e) => setV(e.target.value)} style={{ flex: 1 }} />
      <button className="wfa-btn" disabled={busy || !v} onClick={() => { patch([{ op: "addManifestItem", stepNo, itemNo: nextNo, description: v, direction: "input", mandatory: true }]); setV(""); }}>Add</button>
    </div>
  );
}
